import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Search, 
  MapPin, 
  Building2, 
  DollarSign, 
  Bookmark, 
  BookmarkCheck, 
  ExternalLink, 
  Loader2,
  Filter,
  Briefcase,
  CheckCircle,
  Shield,
  Star,
  Sparkles,
  Target,
  ChevronDown,
  ChevronUp,
  Zap
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPLOYMENT_TYPES = [
  { value: 'FULLTIME', label: 'Full-time' },
  { value: 'PARTTIME', label: 'Part-time' },
  { value: 'CONTRACTOR', label: 'Contractor' },
  { value: 'INTERN', label: 'Internship' },
  { value: 'all', label: 'All Types' },
];

const DATE_POSTED_OPTIONS = [
  { value: 'all', label: 'Any time' },
  { value: 'today', label: 'Today' },
  { value: '3days', label: 'Last 3 days' },
  { value: 'week', label: 'Last week' },
  { value: 'month', label: 'Last month' },
];

const JobSearch = () => {
  const { token } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [savedJobs, setSavedJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [remoteOnly, setRemoteOnly] = useState(false);
  const [activeTab, setActiveTab] = useState('search');
  const [showFilters, setShowFilters] = useState(false);
  const [queryUsed, setQueryUsed] = useState('');
  const [mosSuggestions, setMosSuggestions] = useState(null);
  
  // Advanced filters
  const [employmentType, setEmploymentType] = useState('FULLTIME');
  const [datePosted, setDatePosted] = useState('all');
  const [minSalary, setMinSalary] = useState(0);
  const [maxSalary, setMaxSalary] = useState(200000);
  const [radius, setRadius] = useState(50);

  useEffect(() => {
    fetchJobs();
    fetchSavedJobs();
    fetchMosSuggestions();
  }, [token]);

  const fetchMosSuggestions = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/jobs/mos-suggestions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMosSuggestions(response.data);
    } catch (error) {
      console.error('Failed to fetch MOS suggestions:', error);
    }
  };

  const fetchJobs = async (query = '', location = '', remote = false) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (query) params.append('query', query);
      if (location) params.append('location', location);
      if (remote) params.append('remote', 'true');
      if (employmentType && employmentType !== 'all') params.append('employment_type', employmentType);
      if (minSalary > 0) params.append('min_salary', minSalary.toString());
      if (maxSalary < 200000) params.append('max_salary', maxSalary.toString());
      if (radius !== 50 && location) params.append('radius', radius.toString());
      
      const response = await axios.get(`${API_URL}/api/jobs/search?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 60000 // 60 second timeout
      });
      setJobs(response.data.jobs || []);
      setQueryUsed(response.data.query_used || '');
    } catch (error) {
      console.error('Job search error:', error);
      if (error.code === 'ECONNABORTED') {
        toast.error('Search timed out. Please try again.');
      } else {
        toast.error('Failed to fetch jobs');
      }
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSavedJobs = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/jobs/saved`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSavedJobs(response.data);
    } catch (error) {
      console.error('Failed to fetch saved jobs:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchJobs(searchQuery, locationFilter, remoteOnly);
  };

  const useMosSuggestion = () => {
    if (mosSuggestions?.search_query) {
      setSearchQuery(mosSuggestions.search_query);
      fetchJobs(mosSuggestions.search_query, locationFilter, remoteOnly);
    }
  };

  const saveJob = async (job) => {
    try {
      await axios.post(`${API_URL}/api/jobs/save`, job, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Job saved!');
      fetchSavedJobs();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.info('Job already saved');
      } else {
        toast.error('Failed to save job');
      }
    }
  };

  const unsaveJob = async (jobId) => {
    try {
      await axios.delete(`${API_URL}/api/jobs/saved/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Job removed');
      fetchSavedJobs();
    } catch (error) {
      toast.error('Failed to remove job');
    }
  };

  const markApplied = async (jobId) => {
    try {
      await axios.patch(`${API_URL}/api/jobs/saved/${jobId}/applied`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Marked as applied');
      fetchSavedJobs();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const isJobSaved = (externalJobId) => {
    return savedJobs.some(j => j.external_job_id === externalJobId);
  };

  const getMatchScoreColor = (score) => {
    if (score >= 80) return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    if (score >= 60) return 'bg-amber-100 text-amber-700 border-amber-200';
    if (score >= 40) return 'bg-blue-100 text-blue-700 border-blue-200';
    return 'bg-navy-100 text-navy-600 border-navy-200';
  };

  const JobCard = ({ job, showSaveButton = true }) => (
    <Card className="border-navy-100 dark:border-navy-700 dark:bg-navy-800 hover:shadow-md transition-shadow">
      <CardContent className="p-5">
        <div className="flex justify-between items-start mb-3">
          <div className="flex items-start gap-3 flex-1">
            {job.employer_logo && (
              <img 
                src={job.employer_logo} 
                alt={job.company}
                className="w-12 h-12 rounded-lg object-contain bg-white dark:bg-navy-700 border border-navy-100 dark:border-navy-600"
                onError={(e) => e.target.style.display = 'none'}
              />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <h3 className="font-heading font-semibold text-navy-900 dark:text-white">{job.title}</h3>
                {job.is_veteran_friendly && (
                  <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400 text-xs gap-1">
                    <Shield className="w-3 h-3" />
                    Veteran Friendly
                  </Badge>
                )}
                {job.is_remote && (
                  <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-400 text-xs">
                    Remote
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-4 text-sm text-navy-500 dark:text-navy-400 flex-wrap">
                <span className="flex items-center gap-1">
                  <Building2 className="w-4 h-4" />
                  {job.company}
                </span>
                {job.location && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {job.location}
                  </span>
                )}
              </div>
            </div>
          </div>
          
          {/* Match Score & Save */}
          <div className="flex items-center gap-2 ml-2">
            {job.match_score !== undefined && job.match_score > 0 && (
              <div className={`px-3 py-1.5 rounded-lg border ${getMatchScoreColor(job.match_score)}`}>
                <div className="flex items-center gap-1">
                  <Target className="w-3.5 h-3.5" />
                  <span className="font-semibold text-sm">{job.match_score}%</span>
                </div>
                <span className="text-xs">Match</span>
              </div>
            )}
            {showSaveButton && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => isJobSaved(job.external_job_id) ? null : saveJob(job)}
                className={isJobSaved(job.external_job_id) ? 'text-amber-600' : 'text-navy-400 dark:text-navy-500 hover:text-amber-600'}
                data-testid={`save-job-${job.external_job_id}`}
              >
                {isJobSaved(job.external_job_id) ? (
                  <BookmarkCheck className="w-5 h-5" />
                ) : (
                  <Bookmark className="w-5 h-5" />
                )}
              </Button>
            )}
          </div>
        </div>

        {/* Match Reasons */}
        {job.match_reasons && job.match_reasons.length > 0 && (
          <div className="mb-3 flex flex-wrap gap-1">
            {job.match_reasons.map((reason, idx) => (
              <Badge key={idx} variant="outline" className="text-xs bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800">
                <Sparkles className="w-3 h-3 mr-1" />
                {reason}
              </Badge>
            ))}
          </div>
        )}

        {/* Veteran Relevance Score */}
        {job.veteran_relevance_score > 0 && (
          <div className="mb-3">
            <Badge variant="outline" className="text-xs bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800">
              <Zap className="w-3 h-3 mr-1" />
              Veteran Relevance: {job.veteran_relevance_score}
            </Badge>
          </div>
        )}

        <p className="text-sm text-navy-600 dark:text-navy-300 mb-3 line-clamp-2">{job.description}</p>

        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            {job.salary && (
              <Badge variant="outline" className="text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800">
                <DollarSign className="w-3 h-3 mr-1" />
                {job.salary}
              </Badge>
            )}
            {job.job_type && (
              <Badge variant="outline" className="text-navy-600 dark:text-navy-300 border-navy-200 dark:border-navy-600">
                {job.job_type}
              </Badge>
            )}
            {job.posted_date && (
              <span className="text-xs text-navy-400">
                Posted: {job.posted_date}
              </span>
            )}
            {job.applied && (
              <Badge className="bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-400">
                <CheckCircle className="w-3 h-3 mr-1" />
                Applied
              </Badge>
            )}
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => window.open(job.url, '_blank')}
            className="dark:border-navy-600 dark:text-white dark:hover:bg-navy-700"
          >
            <ExternalLink className="w-4 h-4 mr-1" />
            View
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="job-search-page">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-navy-900 dark:text-white mb-2">Job Search</h1>
          <p className="text-navy-600 dark:text-navy-300">Find veteran-friendly opportunities with AI-powered matching</p>
        </div>

        {/* MOS Suggestions Banner */}
        {mosSuggestions?.suggestions?.length > 0 && (
          <Card className="border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 mb-6">
            <CardContent className="p-4">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-100 dark:bg-amber-800 rounded-lg flex items-center justify-center">
                    <Shield className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <p className="font-medium text-navy-900 dark:text-white">Jobs matching your MOS: {mosSuggestions.mos}</p>
                    <p className="text-sm text-navy-600 dark:text-navy-300">
                      Suggested: {mosSuggestions.suggestions.slice(0, 3).join(', ')}
                    </p>
                  </div>
                </div>
                <Button onClick={useMosSuggestion} className="bg-amber-600 hover:bg-amber-700">
                  <Search className="w-4 h-4 mr-2" />
                  Search MOS-Matched Jobs
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 dark:bg-navy-800">
            <TabsTrigger value="search" className="gap-2 data-[state=active]:dark:bg-navy-700">
              <Search className="w-4 h-4" />
              Search Jobs
            </TabsTrigger>
            <TabsTrigger value="saved" className="gap-2 data-[state=active]:dark:bg-navy-700">
              <Bookmark className="w-4 h-4" />
              Saved ({savedJobs.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="search">
            {/* Search Form */}
            <Card className="border-navy-100 dark:border-navy-700 dark:bg-navy-800 mb-6">
              <CardContent className="p-4">
                <form onSubmit={handleSearch} className="space-y-4">
                  <div className="flex flex-wrap gap-4">
                    <div className="flex-1 min-w-[200px]">
                      <Label className="sr-only">Search</Label>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy-400" />
                        <Input
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder="Job title, skills, or keywords"
                          className="pl-10 dark:bg-navy-700 dark:border-navy-600 dark:text-white"
                          data-testid="job-search-input"
                        />
                      </div>
                    </div>
                    <div className="w-48">
                      <Label className="sr-only">Location</Label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy-400" />
                        <Input
                          value={locationFilter}
                          onChange={(e) => setLocationFilter(e.target.value)}
                          placeholder="City, State"
                          className="pl-10 dark:bg-navy-700 dark:border-navy-600 dark:text-white"
                        />
                      </div>
                    </div>
                    <Button type="submit" className="bg-amber-600 hover:bg-amber-700" data-testid="search-jobs-btn">
                      <Search className="w-4 h-4 mr-2" />
                      Search
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline"
                      onClick={() => setShowFilters(!showFilters)}
                      className="dark:border-navy-600 dark:text-white dark:hover:bg-navy-700"
                    >
                      <Filter className="w-4 h-4 mr-2" />
                      Filters
                      {showFilters ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
                    </Button>
                  </div>

                  {/* Advanced Filters */}
                  {showFilters && (
                    <div className="pt-4 border-t border-navy-100 dark:border-navy-600 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div>
                        <Label className="text-navy-700 dark:text-navy-300 text-sm mb-1.5 block">Employment Type</Label>
                        <Select value={employmentType} onValueChange={setEmploymentType}>
                          <SelectTrigger className="dark:bg-navy-700 dark:border-navy-600">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="dark:bg-navy-800 dark:border-navy-600">
                            {EMPLOYMENT_TYPES.map(type => (
                              <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-navy-700 dark:text-navy-300 text-sm mb-1.5 block">Date Posted</Label>
                        <Select value={datePosted} onValueChange={setDatePosted}>
                          <SelectTrigger className="dark:bg-navy-700 dark:border-navy-600">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="dark:bg-navy-800 dark:border-navy-600">
                            {DATE_POSTED_OPTIONS.map(opt => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-navy-700 dark:text-navy-300 text-sm mb-1.5 block">
                          Salary Range: ${minSalary.toLocaleString()} - ${maxSalary.toLocaleString()}
                        </Label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            value={minSalary}
                            onChange={(e) => setMinSalary(parseInt(e.target.value) || 0)}
                            placeholder="Min"
                            className="w-24 dark:bg-navy-700 dark:border-navy-600 dark:text-white"
                          />
                          <span className="text-navy-400">-</span>
                          <Input
                            type="number"
                            value={maxSalary}
                            onChange={(e) => setMaxSalary(parseInt(e.target.value) || 200000)}
                            placeholder="Max"
                            className="w-24 dark:bg-navy-700 dark:border-navy-600 dark:text-white"
                          />
                        </div>
                      </div>

                      <div>
                        <Label className="text-navy-700 dark:text-navy-300 text-sm mb-1.5 block">
                          Search Radius: {radius} miles
                        </Label>
                        <Slider
                          value={[radius]}
                          onValueChange={([val]) => setRadius(val)}
                          max={100}
                          min={10}
                          step={10}
                          className="mt-3"
                        />
                      </div>

                      <div className="flex items-center gap-2 sm:col-span-2 lg:col-span-4">
                        <Checkbox 
                          id="remote" 
                          checked={remoteOnly}
                          onCheckedChange={setRemoteOnly}
                        />
                        <Label htmlFor="remote" className="text-sm text-navy-700 dark:text-navy-300 cursor-pointer">
                          Remote jobs only
                        </Label>
                      </div>
                    </div>
                  )}
                </form>
              </CardContent>
            </Card>

            {/* Query Used Info */}
            {queryUsed && (
              <div className="mb-4 text-sm text-navy-500 dark:text-navy-400">
                <span>Searching for: </span>
                <span className="font-medium text-navy-700 dark:text-navy-200">{queryUsed}</span>
              </div>
            )}

            {/* Results */}
            {loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-amber-600 mb-4" />
                <p className="text-navy-600 dark:text-navy-300">Finding veteran-friendly jobs...</p>
                <p className="text-sm text-navy-400">Running AI matching on top results</p>
              </div>
            ) : jobs.length > 0 ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-navy-500 dark:text-navy-400">{jobs.length} jobs found</p>
                  <div className="flex items-center gap-2 text-xs text-navy-400">
                    <Target className="w-3.5 h-3.5" />
                    AI Match scores on top results
                  </div>
                </div>
                {jobs.map(job => (
                  <JobCard key={job.external_job_id} job={job} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Briefcase className="w-12 h-12 text-navy-200 dark:text-navy-600 mx-auto mb-4" />
                <h3 className="font-heading font-semibold text-navy-700 dark:text-navy-300 mb-2">No jobs found</h3>
                <p className="text-navy-500 dark:text-navy-400">Try adjusting your search criteria or filters</p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="saved">
            {savedJobs.length > 0 ? (
              <div className="space-y-4">
                <p className="text-sm text-navy-500 dark:text-navy-400">{savedJobs.length} saved jobs</p>
                {savedJobs.map(job => (
                  <Card key={job.job_id} className="border-navy-100 dark:border-navy-700 dark:bg-navy-800 hover:shadow-md transition-shadow">
                    <CardContent className="p-5">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <h3 className="font-heading font-semibold text-navy-900 dark:text-white">{job.title}</h3>
                            {job.match_score && (
                              <Badge className={getMatchScoreColor(job.match_score)}>
                                <Target className="w-3 h-3 mr-1" />
                                {job.match_score}% Match
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-4 text-sm text-navy-500 dark:text-navy-400">
                            <span className="flex items-center gap-1">
                              <Building2 className="w-4 h-4" />
                              {job.company}
                            </span>
                            <span className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {job.location}
                            </span>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => unsaveJob(job.job_id)}
                          className="text-amber-600 hover:text-destructive"
                        >
                          <BookmarkCheck className="w-5 h-5" />
                        </Button>
                      </div>

                      <p className="text-sm text-navy-600 dark:text-navy-300 mb-3 line-clamp-2">{job.description}</p>

                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-3">
                          {job.salary && (
                            <Badge variant="outline" className="text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800">
                              <DollarSign className="w-3 h-3 mr-1" />
                              {job.salary}
                            </Badge>
                          )}
                          {job.applied && (
                            <Badge className="bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-400">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Applied
                            </Badge>
                          )}
                        </div>
                        <div className="flex gap-2">
                          {!job.applied && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => markApplied(job.job_id)}
                              className="dark:border-navy-600 dark:text-white dark:hover:bg-navy-700"
                            >
                              Mark Applied
                            </Button>
                          )}
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => window.open(job.url, '_blank')}
                            className="dark:border-navy-600 dark:text-white dark:hover:bg-navy-700"
                          >
                            <ExternalLink className="w-4 h-4 mr-1" />
                            View
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Bookmark className="w-12 h-12 text-navy-200 dark:text-navy-600 mx-auto mb-4" />
                <h3 className="font-heading font-semibold text-navy-700 dark:text-navy-300 mb-2">No saved jobs yet</h3>
                <p className="text-navy-500 dark:text-navy-400 mb-4">Save jobs you're interested in to track them here</p>
                <Button onClick={() => setActiveTab('search')} variant="outline" className="dark:border-navy-600 dark:text-white">
                  Browse Jobs
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default JobSearch;
